import React, { useEffect, useRef, useState } from 'react';
import { Column, Row } from 'react-foundation';
import OpportunityButton from '../organizations/opportunities/OpportunityButton';
import MediaUploadButton from '../organizations/opportunities/MediaUploadButton';
import * as ArticlesAPI from '../../../api/ArticlesAPI';
import { Editor } from '@tinymce/tinymce-react';
import * as cloudinaryAPI from '../../../api/cloudinaryAPI';
import { OrganizationAutocomplete } from '../shared/OrganizationAutocomplete';
import LoadingSpinner from '../shared/LoadingSpinner';
import { DEFAULT_IMG, DEFAULT_PDF_IMG } from '../../utils/contants';

// TODO: MOVE TO ENV
const _TIDY_API_KEY = 'km2h7c3lhh1mifjf38z5j7lqf7i7fhvx5ltpu1h0fj8fpjs3';

export const configCloudinaryWidget = (
	setCloudinaryUploadWidget,
	handleSuccessfulFileUpload
) => {
	$.getScript('https://upload-widget.cloudinary.com/global/all.js').then(
		() => {
			const tempWidget = cloudinary.createUploadWidget(
				{
					cloudName: 'sponsor-circle',
					uploadPreset: 'pv9mlmap',
					multiple: false,
					sources: [
						'local',
						'instagram',
						'google_drive',
						'facebook',
						'shutterstock',
						'gettyimages',
						'istock',
						'unsplash',
					],
				},
				(error, result) => {
					// This should rarely be the case, but if the cap on gallery files is hit, then immediately close the widget
					if (!error && result && result.event === 'success') {
						handleSuccessfulFileUpload(result);
					} else if (error) {
						console.error(error);
					}
				}
			);

			setCloudinaryUploadWidget(tempWidget);
		}
	);
};

const NewArticle = ({ organizations, articleID, currentUser }) => {
	const articleDataTemplate = {
		id: 0,
		title: '',
		body: '',
		user_id: 0,
		approved: false,
		img_url: '',
		author: '',
		posted_on: '',
		source_name: '',
		source_url: '',
		organization_ids: [],
		file_name: null,
	};

	const editorRef = useRef(null);
	const [articleData, setArticleData] = useState(articleDataTemplate);
	const [headerFileName, setHeaderFileName] = useState(articleData.img_url);
	const [refreshPage, setRefreshPage] = useState(false);
	const [requiredFields, setRequiredFields] = useState([]);
	const [defaultAuthorValue, setDefaultAuthorValue] = useState(() => {
		if (!currentUser?.first_name || currentUser?.last_name) {
			return '';
		}

		return `${currentUser?.first_name} ${currentUser?.last_name}`;
	});
	const emptyFieldMessage = 'Field cannot be left blank!';
	const [isLoading, setIsLoading] = useState(false);
	const [cloudinaryUploadWidget, setCloudinaryUploadWidget] =
		useState(undefined);
	const [currentActiveFileComponentName, setCurrentActiveFileComponentName] =
		useState('');

	useEffect(() => {
		if (articleID) {
			fetchArticleData();
			fetchFieldAttributes();
		}
		configCloudinaryWidget(
			setCloudinaryUploadWidget,
			handleSuccessfulFileUpload
		);
	}, []);

	const handleSuccessfulFileUpload = (result) => {
		const fileName = `${result.info.original_filename}.${result.info.format}`;

		setArticleData((prevState) => ({
			...prevState,
			img_url: result.info.secure_url,
			file_name: fileName,
		}));
	};

	const openCloudinaryWidget = (name) => {
		setCurrentActiveFileComponentName(name);
		cloudinaryUploadWidget.open();
	};

	const fetchArticleData = () => {
		ArticlesAPI.getArticleByID(articleID).then((response) => {
			let article = response.data;

			let orgIds = [];
			// Must convert organizations field to organization_ids here
			for (const org of article?.organizations) {
				orgIds.push({ id: org.id, name: org.legal_name });
			}
			article.organization_ids = orgIds;
			setArticleData(article);
			// TODO - is this still relevant? Seems like it's legacy
			// document.getElementById("organization_ids_select").value = article.organizations[0]?.id;
		});
	};

	const fetchFieldAttributes = () => {
		ArticlesAPI.getFieldAttributes().then((response) => {
			let fieldAttributes = response.data;
			let tempRequiredFields = fieldAttributes?.presenceValidations;
			let currentAuthor = fieldAttributes?.defaultAuthorValue;

			setRequiredFields(tempRequiredFields);
			setDefaultAuthorValue(currentAuthor);

			for (const requiredField of tempRequiredFields) {
				let element = document.getElementById(
					requiredField + '-header'
				);
				if (element) {
					element.textContent += ' *';
				}
			}

			// let authorTextElement =
			// 	document.getElementById('author-input-field');
			// if (authorTextElement) {
			// 	authorTextElement.value = currentAuthor;
			// 	articleData.author = currentAuthor;
			// 	setArticleData(articleData);
			// }
		});
	};

	const areRequiredFieldsFilled = () => {
		let allRequiredFieldsFilled = true;
		let hasScrolledAlready = false;

		for (const requiredField of requiredFields) {
			if (
				(requiredField === 'body' &&
					tinymce.get('textarea').getContent() === '') ||
				(requiredField !== 'body' && articleData[requiredField] === '')
			) {
				allRequiredFieldsFilled = false;
				document.getElementById(
					requiredField + '-empty-label'
				).style.display = 'inline-block';
				if (!hasScrolledAlready) {
					document
						.getElementById(requiredField + '-header')
						.scrollIntoView({ behavior: 'smooth' });
					hasScrolledAlready = true;
				}
			}
		}

		return allRequiredFieldsFilled;
	};

	/**
	 * Handles cancelling of the form, and bringing the user back to the organization page
	 */
	const handleCancel = () => {
		history.back();
	};

	const redirectToArticle = (articleID) => {
		location.assign(`/articles/${articleID}`);
	};

	const handleSubmit = async () => {
		if (areRequiredFieldsFilled()) {
			setIsLoading(true);
			try {
				articleData.body = tinymce.get('textarea').getContent();
				// Must convert organization_ids into proper format
				let tempOrgIds = [];
				for (const orgId of articleData.organization_ids) {
					tempOrgIds.push(orgId.id);
				}
				articleData.user_id = currentUser.id;
				articleData.organization_ids = tempOrgIds;
				// the backend wants ids and will get confused if you pass the orgs + org_ids
				delete articleData['organizations'];

				// If articleID exists, that means we are in edit mode, so just do PUT
				if (articleID) {
					let response = await ArticlesAPI.putArticle(
						articleID,
						articleData
					);
					let article = response.data;
					redirectToArticle(article.id);
				} else {
					let response = await ArticlesAPI.postArticle(articleData);
					let article = response.data;
					redirectToArticle(article.id);
				}
			} catch (e) {
				console.error(e);
			}
		}
		setIsLoading(false);
	};

	/**
	 * Sets the minimum dates
	 */
	const setMinDates = () => {
		let today = new Date();
		let dd = String(today.getDate()).padStart(2, '0');
		let mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
		let yyyy = today.getFullYear();
		today = yyyy + '-' + mm + '-' + dd;
		document.getElementById('posted_on').min = today;
	};

	/**
	 * Handles submission of media, in either a URL to the picture/video or the file itself
	 * @param name the name of the component submitting the file
	 * @param link the link to the URL/file
	 */
	const handleMediaSubmission = (name, link) => {
		console.log(name);
		console.log(link);
		articleData['img_url'] = link;
		setArticleData(articleData);
		console.log(articleData);
	};

	const handleClearingMedia = () => {
		articleData['img_url'] = '';
		setArticleData(articleData);
		setRefreshPage(!refreshPage);
		console.log(articleData);
	};

	const handleChange = (event) => {
		articleData[event.target.name] = event.target.value;
		setArticleData(articleData);

		if (requiredFields.includes(event.target.name)) {
			if (event.target.value === '') {
				document.getElementById(
					event.target.name + '-empty-label'
				).style.display = 'inline-block';
			} else {
				document.getElementById(
					event.target.name + '-empty-label'
				).style.display = 'none';
			}
		}
	};

	const handleOrganizationAssociatedChange = (event) => {
		articleData.organization_ids.push({
			id: event.target.value,
			name: event.target.selectedOptions[0].text,
		});
		setArticleData(articleData);
		setRefreshPage(!refreshPage);
	};

	const getTodaysDate = () => {
		var today = new Date();
		var dd = String(today.getDate()).padStart(2, '0');
		var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
		var yyyy = today.getFullYear();

		return yyyy + '-' + mm + '-' + dd;
	};

	/**
	 * Handles clicking of a checkbox under a tag section.
	 * @param event the event of the checkbox clicked
	 */
	const handleCheckboxes = (event) => {
		console.log(event.target);
		let found = false;

		for (const org of articleData.organization_ids) {
			if (String(org.id) === String(event.target.id)) {
				found = true;
			}
		}
		console.log(found);
		if (!found) {
			articleData['organization_ids'].push({
				id: parseInt(event.target.name),
				name: event.target.value,
			});
			setArticleData(articleData);
			console.log(articleData);
			setRefreshPage(!refreshPage);
		} else {
			// If the target has just become unchecked, then remove it from our stored orgData object
			articleData['organization_ids'].forEach((organization, index) => {
				if (String(organization.id) === String(event.target.id)) {
					articleData['organization_ids'].splice(index, 1);
					setArticleData(articleData);
					console.log(articleData);
					setRefreshPage(!refreshPage);
				}
			});
		}
	};

	// Handles
	const addDefaultSrc = (e) => {
		let DEFAULT_IMG_SRC = DEFAULT_IMG;

		if (articleData.file_name && articleData.file_name.includes('.pdf')) {
			DEFAULT_IMG_SRC = DEFAULT_PDF_IMG;
		}

		e.target.src = DEFAULT_IMG_SRC;
	};

	// Components
	const DisplayedMediaName = () => {
		if (!articleData.img_url) {
			return null;
		}

		// TODO: Fix -> upload img, upload url -> fileName doesnt change
		let fileName = cloudinaryAPI.formatCloudinaryURLToDisplayToUser(
			articleData.file_name || articleData.img_url
		);
		return (
			<p className="body-font image-name">
				Current file:{' ' + fileName}
			</p>
		);
	};

	const ImagePreview = () => {
		if (!articleData.img_url) {
			return null;
		}

		let imagePreview = articleData.img_url;

		if (
			(imagePreview && imagePreview.includes('.pdf')) ||
			(articleData.file_name && articleData.file_name.includes('.pdf'))
		) {
			imagePreview = DEFAULT_PDF_IMG;
		}

		return (
			<img
				onError={addDefaultSrc}
				className="logo-img"
				src={[imagePreview || DEFAULT_IMG_SRC]}
				alt=""
			/>
		);
	};

	return (
		<div className="create-article">
			{/*<Row>*/}
			{/*  <Column small={12}>*/}
			{/*    <OpportunityButton text="Back to organization profile" className="back-link" handleSubmit={handleCancel}*/}
			{/*                       isBackLink={true} leftButtonText="Cancel" rightButtonText="Exit"*/}
			{/*                       headerText="You have unsaved content. Would you like to exit and lose your work?"/>*/}
			{/*  </Column>*/}
			{/*  <div className="bottom-30px-margin"/>*/}
			{/*</Row>*/}
			<Row>
				<Column small={12}>
					{articleID ? (
						<h1 className="bottom-30px-margin">
							Edit news article
						</h1>
					) : (
						<h1 className="bottom-30px-margin">
							Add a news article
						</h1>
					)}
					<p className="header-font" id="title-header">
						Title
					</p>
					<input
						type="text"
						placeholder="Enter a title for your news article"
						onChange={(e) => {
							handleChange(e);
						}}
						name="title"
						className="body-font-important"
						defaultValue={articleData?.title}
					/>
					<label className="empty-field-label" id="title-empty-label">
						{emptyFieldMessage}
					</label>
				</Column>
			</Row>
			<Row>
				<Column small={12}>
					<h6 className="header-font">Header image</h6>
					<ImagePreview />

					<div className="header-image-container">
						<MediaUploadButton
							name="header_media"
							handleMediaSubmission={handleMediaSubmission}
							text="Upload header image"
							icon={true}
							className="header-media-button float-left"
							setMediaFileName={setHeaderFileName}
							handleClearingMedia={handleClearingMedia}
							openCloudinaryWidget={openCloudinaryWidget}
							setArticleData={setArticleData}
						/>
					</div>
					<DisplayedMediaName />
				</Column>
			</Row>
			<Row className="top-10px-margin">
				<Column small={12} medium={6}>
					<h6 className="header-font">Author</h6>
					<input
						type="text"
						placeholder="Enter an author for your news article"
						onChange={(e) => {
							handleChange(e);
						}}
						defaultValue={defaultAuthorValue}
						name="author"
						className="body-font-important"
						id="author-input-field"
					/>
				</Column>

				<Column small={12} medium={6}>
					<h6 className="header-font">Date Posted</h6>
					<input
						type="date"
						onFocus={setMinDates}
						id="posted_on"
						onChange={(e) => {
							handleChange(e);
						}}
						name="posted_on"
						className="body-font-important"
						defaultValue={
							articleData?.posted_on
								? articleData.posted_on
								: getTodaysDate()
						}
					/>
				</Column>
			</Row>
			<Row>
				<Column small={12}>
					<h6 className="header-font" id="body-header">
						Article
					</h6>
					<Editor
      					apiKey={_TIDY_API_KEY}
						onInit={(evt, editor) => (editorRef.current = editor)}
						initialValue={articleData.body}
						init={{
							height: 500,
							menubar: 'insert',
							plugins: [
								'advlist autolink lists link image charmap print preview anchor',
								'searchreplace visualblocks code fullscreen',
								'insertdatetime media table paste code help wordcount',
							],
							toolbar:
								'undo redo | formatselect | ' +
								'bold italic backcolor | alignleft aligncenter ' +
								'alignright alignjustify | bullist numlist outdent indent | ' +
								'removeformat | link image | help',
							content_style:
								'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
							file_picker_types: 'file image media',
							ally_advanced_options: true,
							file_picker_callback: function (cb, value, meta) {
								var input = document.createElement('input');
								input.setAttribute('type', 'file');
								input.setAttribute('accept', 'image/*');
								input.onchange = function () {
									var file = this.files[0];

									var reader = new FileReader();
									reader.onload = function () {
										/*
                      Note: Now we need to register the blob in TinyMCEs image blob
                      registry. In the next release this part hopefully won't be
                      necessary, as we are looking to handle it internally.
                    */
										var id =
											'blobid' + new Date().getTime();
										var blobCache =
											tinymce.activeEditor.editorUpload
												.blobCache;
										var base64 =
											reader.result.split(',')[1];
										var blobInfo = blobCache.create(
											id,
											file,
											base64
										);
										blobCache.add(blobInfo);

										/* call the callback and populate the Title field with the file name */
										cb(blobInfo.blobUri(), {
											title: file.name,
										});
									};
									reader.readAsDataURL(file);
								};

								input.click();
							},
						}}
						id="textarea"
					/>
					<label className="empty-field-label" id="body-empty-label">
						{emptyFieldMessage}
					</label>
				</Column>
			</Row>
			<Row>
				<Column small={12}>
					<h6
						className="header-font"
						id="associated-organizations-header"
					>
						Organizations associated with this article
					</h6>
					<OrganizationAutocomplete
						organizations={organizations}
						data={articleData}
						setData={setArticleData}
						type="NewArticle"
					/>
				</Column>
			</Row>
			<Row className="top-10px-margin">
				{isLoading ? (
					<LoadingSpinner message={'Publishing...'} />
				) : (
					<></>
				)}
				<Column small={12}>
					<OpportunityButton
						text="Cancel"
						className="secondary-button float-left"
						handleSubmit={handleCancel}
						isBackLink={false}
						leftButtonText="Cancel"
						rightButtonText="Exit"
						headerText="You have unsaved content. Would you like to exit and lose your work?"
					/>
					<OpportunityButton
						text="Publish"
						className="primary-button float-right mobile-button-margin-resizing"
						handleSubmit={handleSubmit}
						isBackLink={false}
						leftButtonText="Cancel"
						rightButtonText="Continue"
						headerText="Your article will be visible to the public once published."
					/>
				</Column>
			</Row>
		</div>
	);
};

export default NewArticle;
